import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { useCallback } from 'react';

import { CardStatus, CardType } from '@pflegia/types/common/job-position/card';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { MatchDialogueStoreData } from '@pflegia/ui/stores/match-dialogue.store';

import Button, { ButtonVariant, getButtonClassName } from '../button/Button.component';
import LinkWithLoader from '../link-with-loader/LinkWithLoader.component';
import SignUpButton from './SignUpButton.component';

const BUTTON_VARIANT_BY_STATUS: Record<CardStatus, ButtonVariant> = {
  accepted: 'primary',
  declined: 'decline',
  pending: 'primary',
  feedback: 'primary',
  reAcceptable: 'primary',
};

export interface OffersDialogueButtonProps {
  status?: CardStatus;
  type?: CardType;
  label?: string;
  className?: string;
  jobPositionId: string;
  jobTitle: string;
  openDialog: (props: MatchDialogueStoreData | unknown) => void;
  employerName?: string;
  colorScheme?: ButtonVariant;
  loading?: boolean;
  chatUrl?: string;
  jobPositionAttributes?: Record<string, unknown>;
  onApplyAndOnAccept: VoidFunction;
}

/**
 * @description This component is responsible for rendering the button that opens the offers dialogue.
 *
 * The dialogue must be shown by the parent component.
 */

const OffersDialogueButton: React.FC<OffersDialogueButtonProps> = ({
  className,
  label,
  status = 'pending',
  type = 'default',
  openDialog,
  colorScheme = 'apply',
  loading = false,
  chatUrl,
  jobPositionAttributes,
  onApplyAndOnAccept,
  jobPositionId,
  jobTitle,
  employerName,
}) => {
  const { t } = useTranslation('job', { keyPrefix: 'dialogue' });
  const color = BUTTON_VARIANT_BY_STATUS[status];
  const isDisabled = (type === 'invitation' && status !== 'pending') || (type === 'application' && status !== 'accepted');
  const authenticated = useAuth();

  const handleCtaClick = useCallback(() => {
    if (status === 'pending' && !isDisabled && authenticated) {
      onApplyAndOnAccept();
      return;
    }
    openDialog({
      status,
      type,
      jobPositionId,
      title: jobTitle,
      employerName,
    } as MatchDialogueStoreData);
  }, [authenticated, employerName, isDisabled, jobPositionId, jobTitle, onApplyAndOnAccept, openDialog, status, type]);

  const classNames = clsx('px-8 py-3 xl:px-12 flex items-center justify-center', className);

  const loggedInCta =
    status === 'accepted' ? (
      <LinkWithLoader href={chatUrl ?? '#'} data-testid='offers-button-accepted' className={getButtonClassName('apply', classNames)}>
        {label ?? t(status)}
      </LinkWithLoader>
    ) : (
      <Button
        data-testid={`offers-button-${status}`}
        colorScheme={className?.includes('text-error') ? 'secondary' : color}
        className={classNames}
        disabled={isDisabled}
        onClick={handleCtaClick}
        label={label ?? t(status)}
        isLoading={loading}
      />
    );

  if (authenticated) return loggedInCta;

  return (
    <SignUpButton
      data-testid='sign-up-dialog-button'
      label={label ?? t(status)}
      colorScheme={colorScheme}
      className={classNames}
      jobPositionAttributes={jobPositionAttributes}
      onApply={onApplyAndOnAccept}
    />
  );
};

export default OffersDialogueButton;
